import React, { useState } from 'react';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Search,
  Globe,
  Shield,
  Plane,
  Home,
  CreditCard,
  FileCheck,
  Star,
  ArrowRight,
  DollarSign,
} from 'lucide-react';
import { motion } from 'framer-motion';

const VAS_SERVICES = [
  {
    id: 1,
    name: 'Travel Insurance',
    description: 'Comprehensive travel insurance coverage',
    icon: Shield,
    price: '$99',
    commission: '8%',
    rating: 4.8,
  },
  {
    id: 2,
    name: 'Airport Pickup',
    description: 'Reliable airport pickup service',
    icon: Plane,
    price: '$45',
    commission: '5%',
    rating: 4.9,
  },
  {
    id: 3,
    name: 'SIM Card Package',
    description: 'International SIM with data plans',
    icon: Globe,
    price: '$25',
    commission: '10%',
    rating: 4.7,
  },
  {
    id: 4,
    name: 'Temporary Housing',
    description: 'Short-term accommodation service',
    icon: Home,
    price: '$150/week',
    commission: '7%',
    rating: 4.6,
  },
  {
    id: 5,
    name: 'Forex Services',
    description: 'Best exchange rates for transfers',
    icon: CreditCard,
    price: 'Variable',
    commission: '2%',
    rating: 4.8,
  },
  {
    id: 6,
    name: 'Document Attestation',
    description: 'Official document verification',
    icon: FileCheck,
    price: '$75',
    commission: '12%',
    rating: 4.5,
  },
];

const gradientStyles = [
  'bg-gradient-to-br from-pink-500 to-rose-500',
  'bg-gradient-to-br from-teal-400 to-cyan-500',
  'bg-gradient-to-br from-purple-500 to-indigo-500',
  'bg-gradient-to-br from-blue-500 to-indigo-600',
];

export default function AgentVASServices() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredServices = VAS_SERVICES.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-vas-services">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">
            VAS Services
          </h1>
          <p className="text-gray-500">
            Value-Added Services to offer your students
          </p>
        </div>

        {/* Search */}
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search services..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 border-gray-200"
            />
          </div>
        </Card>

        {/* Services Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="vas-services-grid"
        >
          {filteredServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className={`p-6 text-white border-0 shadow-lg rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                    gradientStyles[index % gradientStyles.length]
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
                      <span className="text-sm font-medium">
                        {service.rating}
                      </span>
                    </div>
                  </div>

                  <h3 className="font-semibold text-lg mb-2">{service.name}</h3>
                  <p className="text-sm text-white/80 mb-4">
                    {service.description}
                  </p>

                  <div className="flex items-center justify-between mb-5">
                    {/* Disabled Price */}
                    {/* <Badge className="bg-white/10 text-white/60 line-through border border-white/20">
                      {service.price}
                    </Badge> */}

                    <div className="flex items-center gap-1 text-white">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {service.commission} commission
                      </span>
                    </div>
                  </div>

                  {/* Premium Disabled Button */}
                  <Button
                    disabled
                    className="w-full bg-white text-gray-400 border border-white/40 shadow-none cursor-not-allowed font-medium"
                  >
                    Coming Soon
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </AgentLayout>
  );
}
